import { plural } from "../types";

/**
 * The canonical message set. Every other locale is typed against
 * `typeof en` (see `../messages.ts`), so adding a key here is a compile
 * error in fr/de/pl/zh-Hans until each one answers it — which is the point.
 *
 * What is deliberately absent: the app name, the tagline and the logo.
 * Those are the operator's brand (`lib/config.ts`, set per deployment) and
 * are interpolated into these strings rather than translated.
 */
export const en = {
  common: {
    copy: "Copy",
    copied: "Copied",
    optional: "Optional",
    show: "Show",
    hide: "Hide",
    loading: "Loading…",
    on: "On",
    off: "Off",
  },

  language: {
    label: "Language",
  },

  header: {
    encrypted: "end-to-end encrypted",
  },

  /** Byte-size units, as `formatBytes` renders them. */
  bytes: {
    b: "{size} B",
    kb: "{size} KB",
    mb: "{size} MB",
    gb: "{size} GB",
  },

  /**
   * Durations in two grammatical shapes, because one does not cover both
   * places they appear. `standalone` is a label on its own (an expiry chip,
   * the receipt's "Expires" value); `inSentence` is the form that follows
   * "expires in" in the policy line. English and French inflect neither, but
   * German takes the dative there ("in 7 Tagen", not "7 Tage") and Polish
   * the accusative ("za 1 godzinę", not "1 godzina") — so the split is not
   * optional, it just happens to be invisible here.
   */
  duration: {
    standalone: {
      hours: plural({ one: "{count} hour", other: "{count} hours" }),
      days: plural({ one: "{count} day", other: "{count} days" }),
      weeks: plural({ one: "{count} week", other: "{count} weeks" }),
    },
    inSentence: {
      hours: plural({ one: "{count} hour", other: "{count} hours" }),
      days: plural({ one: "{count} day", other: "{count} days" }),
      weeks: plural({ one: "{count} week", other: "{count} weeks" }),
    },
  },

  create: {
    mode: { text: "Text", file: "File" },
    textPlaceholder: "Paste a password, an API key, a private key…",

    generator: {
      open: "Generate password",
      close: "Close generator",
      length: "Length",
      lowercase: "Lowercase (a–z)",
      uppercase: "Uppercase (A–Z)",
      digits: "Numbers (0–9)",
      symbols: "Symbols (!@#…)",
      excludeAmbiguous: "Avoid look-alikes (0/O, 1/l/I)",
      needCharset: "Pick at least one character type.",
      generate: "Generate and insert",
      note: "Generated in your browser. It goes in at your cursor in the box below, or at the end if you haven’t placed one.",
    },

    dropzone: {
      title: "Drop a file here",
      /** `{browse}` is the clickable word; `{size}` the upload ceiling. */
      hint: "or {browse} — up to {size}",
      browse: "browse",
      note: "encrypted before it leaves this device",
      remove: "Remove",
    },

    encStatus: "Encrypted in your browser. The key lives in the link, never on our servers.",

    expiry: {
      label: "Expires after",
      custom: "Custom",
      unitHours: "Hours",
      unitDays: "Days",
      unitWeeks: "Weeks",
    },

    oneTime: {
      title: "Destroy after one view",
      description: "The secret is wiped the moment it is opened. A second visit sees nothing.",
    },

    allowedEmail: {
      label: "Restrict to one email address",
      hint: "Everyone who opens the link verifies an email with a one-time code. Naming an address here means only that person can pass.",
      placeholder: "only-this-person@example.com",
    },

    notifyEmail: {
      title: "Notify me when viewed",
      description: "A read receipt goes out each time the secret is opened, naming the address that opened it.",
      label: "Send the receipt to",
      placeholder: "you@example.com",
    },

    developer: {
      title: "Developer options",
      webhookUrlLabel: "Webhook URL",
      webhookUrlHint: "We POST a JSON event on creation and on every view.",
      webhookSecretLabel: "Webhook signing secret",
      webhookSecretPlaceholder: "at least 16 characters",
      /** `{header}` renders as <code>x-sss-signature</code>. */
      webhookSecretHint:
        "Used to sign the payload (HMAC-SHA256, sent as the {header} header). Required whenever a webhook URL is set.",
    },

    /** One whole sentence per case — never assembled from clauses. */
    policy: {
      open: "This link expires in {expiry}. Anyone with the link can open it after verifying an email.",
      openOneTime:
        "This link expires in {expiry} or the moment it is opened, whichever comes first. Anyone with the link can open it after verifying an email.",
      restricted: "This link expires in {expiry}. Only {email} can open it.",
      restrictedOneTime:
        "This link expires in {expiry} or the moment it is opened, whichever comes first. Only {email} can open it.",
    },

    submit: "Create secret link",
    submitting: "Encrypting…",

    error: {
      needText: "Enter some text to share.",
      needFile: "Choose a file to share.",
      needNotifyEmail: "Enter an address for the read receipt, or turn the notification off.",
      webhookSecretTooShort: "Webhook signing secret must be at least 16 characters.",
      generic: "Something went wrong creating the secret.",
    },

    result: {
      title: "Your link is ready",
      subtitle: "Send it however you like. Without the fragment after the hash, the link is useless.",
      linkCaption: "the clay-coloured part is the decryption key — we never receive it",
      expires: "Expires",
      destroyAfterOneView: "Destroy after one view",
      restrictedTo: "Restricted to",
      anyVerifiedEmail: "Any verified email",
      createAnother: "Create another",
    },
  },

  retrieve: {
    invalidLink: {
      title: "Invalid link",
      /** `{hash}` renders as <code>#</code>. */
      body: "This link is missing its decryption key. Make sure you copied the entire URL — everything after the {hash} included.",
      note: "If you reloaded this page, open the original link again: the key is taken out of the address bar the moment the page loads, so it can’t end up in your history, a screenshot, or anything this site reports.",
    },

    notFound: {
      title: "Secret not found",
      body: "This secret has expired, was already viewed (if it was a one-time secret), or never existed.",
    },

    alreadyRetrieved: {
      title: "Already opened",
      body: "This was a one-time secret, and someone has already opened it. It can’t be read again — not by you, and not by anyone else.",
      note: "If that someone was you and the download didn’t finish, try the link again within the next few minutes. After that the secret is gone for good, and whoever sent it will need to share it again.",
    },

    decrypting: "Decrypting…",

    fatalError: {
      title: "Something went wrong",
      cannotReachServer: "Could not reach the server.",
      cannotRequestCode: "Could not request a verification code.",
      cannotVerifyCode: "Could not verify the code.",
      decryptionFailed: "Decryption failed — the link may be corrupted or incomplete.",
    },

    email: {
      eyebrow: "Secret waiting",
      title: "Someone sent you something",
      body: "Verify your email to open it. The code is valid for ten minutes.",
      /** Prefixed to `body` for a file secret. */
      fileLine: "File: {filename} ({size}). ",
      label: "Your email",
      placeholder: "you@example.com",
      submit: "Send code",
      submitting: "Sending…",
      restricted: "This secret is restricted to a specific email address, and that isn't it.",
      locked: "Too many incorrect attempts. Request a new code.",
    },

    otp: {
      title: "Enter your code",
      body: "We sent a one-time code to {email}.",
      label: "One-time code",
      submit: "Unlock secret",
      submitting: "Verifying…",
      oneTimeNote: "Opening this destroys it. Copy what you need before you close the tab.",
      resend: "Resend code",
      resendIn: "Resend in {seconds}s",
      incorrect: "Incorrect code.",
      incorrectWithAttempts: plural({
        one: "Incorrect code ({count} attempt left).",
        other: "Incorrect code ({count} attempts left).",
      }),
    },

    success: {
      textTitle: "The secret",
      fileTitle: "Your file",
      destroyedOnServer: "destroyed on server",
      clickToReveal: "Click to reveal",
      sendOneBack: "Send one back",
      fileDownloaded: "{filename} was decrypted in your browser and downloaded.",
      /** `{email}` and `{time}` are the gate this retrieval passed. */
      destroyedNotice:
        "This secret has been destroyed on the server. Reloading this page will show nothing. Verified as {email} at {time}.",
    },
  },

  /**
   * The reply composer, reached from "Send one back" by a recipient who
   * has just passed the OTP gate. Only shown where the operator has turned
   * replies on — see SSO_REPLY_ENABLED.
   */
  reply: {
    title: "Send something back",
    /** `{email}` is whoever sent the secret that was just opened. */
    intro: "This goes to {email}, and only they will be able to open it. You can send one reply.",
    /** `{email}` is the address this browser verified a moment ago. */
    receiptTo: "The receipt goes to {email}, the address you just verified.",
    expired: {
      title: "This reply window has closed",
      body: "A reply can only be sent from the page where you opened a secret, and only for a short while afterwards. Ask whoever sent it to share a new link.",
    },
  },

  auth: {
    signInRequired: "Sign in is required to create a new link.",
    /**
     * Shown in place of the create page when a session that *was* valid has
     * lapsed — see AuthGate. Says so plainly, including the part people
     * would otherwise discover the hard way.
     */
    expired: {
      title: "Your session has expired",
      body: "You were signed out because this page had been open for a while. Sign in again to create a link. Anything you had already typed was not kept.",
    },
    signIn: "Sign in",
    signedInAs: "Signed in as {email}",
    signOut: "Sign out",
  },

  /**
   * API failures a person can act on. Anything not listed falls back to
   * `create.error.generic` rather than showing the raw `error` code.
   */
  apiError: {
    rateLimited: "Too many requests. Wait a moment and try again.",
    tooLarge: "That file is too large to share.",
    unauthenticated: "Your session has expired. Sign in again to create a link.",
    webhookHostNotPermitted: "That webhook URL points somewhere this server will not send to.",
    webhooksDisabled: "Webhooks are turned off on this server.",
    validationFailed: "Some of the details above weren't accepted. Check them and try again.",
  },
};
